import type { ReactNode } from "react";

import { IgDialog, IgTypography } from "@intelligems/ig-theme";

interface DiscardChangesDialogProps {
  open: boolean;
  mode?: "discard" | "leave";
  /** What is being left, in the domain's words: "rule", "custom provider". */
  entityLabel: string;
  /** The saved entity name used by the rule-specific discard copy. */
  entityName?: string | undefined;
  /** Creation copy for a rule that does not have a saved version yet. */
  leaveSubtitle?: ReactNode;
  /**
   * The saved version that remains live after an exit, e.g. "v12". Omit it
   * when the rule does not have a published version.
   */
  liveLabel?: string | undefined;
  onKeepEditing: () => void;
  onDiscard: () => void;
}

/**
 * The shared confirmation behind leaving a workspace.
 *
 * A dirty workspace warns about lost edits. A clean creation workspace instead
 * explains whether the rule is still being built or is already saved.
 */
export const DiscardChangesDialog = ({
  open,
  mode = "discard",
  entityLabel,
  entityName,
  leaveSubtitle,
  liveLabel,
  onKeepEditing,
  onDiscard,
}: DiscardChangesDialogProps) => {
  const isLeave = mode === "leave";

  return (
    <IgDialog
      showDialog={open}
      showCloseIcon
      shouldNotClose
      title={isLeave ? "Leave this rule?" : "Discard changes?"}
      subtitle={
        isLeave
          ? (leaveSubtitle ??
            (liveLabel
              ? `It's live as ${liveLabel} and saved under Rules — you can pick it up anytime.`
              : "It's saved under Rules — you can pick it up anytime."))
          : entityName
            ? `Your unsaved edits to ${entityName} will be lost.`
            : `Your unsaved edits to this ${entityLabel} will be lost.`
      }
      yesButtonText={isLeave ? "Stay" : "Discard changes"}
      noButtonText={isLeave ? "Leave" : "Keep editing"}
      yesButtonColor={isLeave ? "primary" : "error"}
      handleYesClick={isLeave ? onKeepEditing : onDiscard}
      handleNoClick={isLeave ? onDiscard : onKeepEditing}
      handleClose={onKeepEditing}
      content={
        !isLeave && !entityName ? (
          <IgTypography color="text.secondary" variant="body2">
            {liveLabel
              ? `${liveLabel} stays live — nothing shoppers see changes either way.`
              : // Not "nothing is published yet": the caller omits `liveLabel`
                // both for a rule with no version AND for a caller that has no
                // version vocabulary to offer, and the shopper-facing fact is
                // the same in both cases.
                "Nothing is live yet, so nothing shoppers see changes either way."}
          </IgTypography>
        ) : undefined
      }
    />
  );
};
